import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '../ui/dialog';
import { API } from '@/App';
import { PenLine, Trash2, Loader2, FileSignature } from 'lucide-react';

// Finger / stylus / mouse signature pad (DPR-aware).
const SignaturePad = ({ onChange }) => {
  const ref = useRef(null); const drawing = useRef(false); const inked = useRef(false);
  useEffect(() => {
    const c = ref.current; const ctx = c.getContext('2d');
    const setup = () => {
      const r = c.getBoundingClientRect(); const dpr = window.devicePixelRatio || 1;
      c.width = Math.max(1, Math.round(r.width * dpr)); c.height = Math.max(1, Math.round(r.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0E2A47';
    };
    setup();
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const start = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); inked.current = true; e.preventDefault(); };
    const end = () => { if (drawing.current) { drawing.current = false; onChange(inked.current ? c.toDataURL('image/png') : ''); } };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move); window.addEventListener('mouseup', end);
    c.addEventListener('touchstart', start, { passive: false }); c.addEventListener('touchmove', move, { passive: false }); c.addEventListener('touchend', end);
    window.addEventListener('resize', setup);
    return () => { c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move); window.removeEventListener('mouseup', end); c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move); c.removeEventListener('touchend', end); window.removeEventListener('resize', setup); };
  }, [onChange]);
  const clear = () => { const c = ref.current; const ctx = c.getContext('2d'); ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, c.width, c.height); ctx.restore(); inked.current = false; onChange(''); };
  return (
    <div>
      <canvas ref={ref} width={460} height={120} data-testid="w9-signature-pad"
        className="w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-white" />
      <button type="button" onClick={clear} data-testid="w9-signature-clear"
        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
        <Trash2 size={12} /> Clear signature
      </button>
    </div>
  );
};

const field = 'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#1763B6]';
const lbl = 'text-[11px] font-bold uppercase tracking-wide text-slate-500';

export default function ProviderW9EsignDialog({ provider, token, onClose, onSigned }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [meta, setMeta] = useState(null);
  const [name, setName] = useState(provider?.company_name || '');
  const [businessName, setBusinessName] = useState('');
  const [classification, setClassification] = useState('individual');
  const [tinType, setTinType] = useState('ssn');
  const [tin, setTin] = useState('');
  const [address, setAddress] = useState('');
  const [cityStateZip, setCityStateZip] = useState('');
  const [sig, setSig] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/service-providers/documents/w9/meta`, h).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  async function submit() {
    if (!name.trim()) return toast.error('Enter the name as shown on the income tax return');
    if ((tin || '').replace(/\D/g, '').length !== 9) return toast.error('Enter a valid 9-digit TIN');
    if (!sig) return toast.error('Please draw a signature');
    if (!consent) return toast.error('Please confirm the electronic-signature consent');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/service-providers/${provider.id}/documents/w9/esign`, {
        name: name.trim(), business_name: businessName.trim(), classification_box: classification,
        tin_type: tinType, tin: tin.replace(/\D/g, ''), address: address.trim(),
        city_state_zip: cityStateZip.trim(), signature_png: sig, consent: true,
      }, h);
      toast.success('W-9 e-signed \u2014 attached as On File \u00b7 Awaiting Verification');
      onSigned?.(r.data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not e-sign the W-9');
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="w9-esign-dialog"
        onInteractOutside={(e) => e.preventDefault()}>
        <div className="flex items-center gap-2">
          <FileSignature size={18} className="text-[#0E2A47]" />
          <h3 className="text-lg font-bold text-[#0E2A47]">Fill &amp; Sign IRS Form W-9</h3>
        </div>
        <p className="text-xs text-slate-500">
          {meta ? `${meta.title} \u00b7 ${meta.revision}` : 'Loading official IRS Form W-9\u2026'} — the real IRS form is filled and
          signed in-app. A Global Administrator still verifies it before it counts toward qualification.
        </p>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block sm:col-span-2"><span className={lbl}>Name (as shown on your income tax return) *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="w9-name" className={field} />
          </label>
          <label className="block sm:col-span-2"><span className={lbl}>Business name / disregarded entity (if different)</span>
            <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="w9-business-name" className={field} />
          </label>
          <label className="block sm:col-span-2"><span className={lbl}>Federal tax classification *</span>
            <select value={classification} onChange={(e) => setClassification(e.target.value)} data-testid="w9-classification" className={field}>
              {(meta?.classifications || [{ value: 'individual', label: 'Individual / sole proprietor' }]).map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className="block sm:col-span-2"><span className={lbl}>Address (number, street, apt/suite) *</span>
            <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="w9-address" className={field} />
          </label>
          <label className="block sm:col-span-2"><span className={lbl}>City, state, ZIP *</span>
            <input value={cityStateZip} onChange={(e) => setCityStateZip(e.target.value)} data-testid="w9-city-state-zip" className={field} />
          </label>
          <label className="block"><span className={lbl}>TIN type *</span>
            <select value={tinType} onChange={(e) => setTinType(e.target.value)} data-testid="w9-tin-type" className={field}>
              <option value="ssn">SSN</option>
              <option value="ein">EIN</option>
            </select>
          </label>
          <label className="block"><span className={lbl}>{tinType === 'ein' ? 'EIN' : 'SSN'} (9 digits) *</span>
            <input value={tin} onChange={(e) => setTin(e.target.value)} data-testid="w9-tin"
              inputMode="numeric" placeholder={tinType === 'ein' ? 'XX-XXXXXXX' : 'XXX-XX-XXXX'} className={field} />
          </label>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="mb-1 text-xs font-bold uppercase text-slate-500">Draw signature (Signature of U.S. person)</div>
          <SignaturePad onChange={setSig} />
          <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="w9-consent" className="mt-0.5" />
            <span>Under penalties of perjury I certify the information above is true and correct per the official IRS Form W-9
              certifications, I adopt the drawn mark above as my legal electronic signature, and I consent to sign
              electronically (ESIGN / UETA).</span>
          </label>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm" data-testid="w9-esign-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="w9-esign-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} Sign &amp; attach
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
